/**
 * Componente optimizado para mostrar el avatar del usuario actual.
 * Versión mejorada que usa el hook centralizado useCurrentUserProfile.
 * Elimina duplicación de lógica y mantiene consistencia en toda la app.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {number} props.customSize - Tamaño personalizado del avatar (default: 44)
 * @param {number} props.bradius - Border radius personalizado (default: 8)
 * @param {boolean} props.showInfo - Muestra información adicional del usuario
 * @param {boolean} props.showName - Muestra el nombre del usuario
 * @param {function} props.onPress - Función personalizada para el evento onPress
 * @param {boolean} props.showLoadingIndicator - Muestra indicador de carga (default: true)
 * @param {string} props.placeholder - Texto placeholder para el nombre
 */

import React from 'react';
import { useCurrentUserProfile } from '../../../hooks/useCurrentUserProfile';
import { defaultImage } from '../../../constants/defaultImage';
import CachedAvatarImage from '../../../components/CachedAvatarImage';

export default function MyUserAvatar({
  customSize = 54,
  bradius = 50,
  showInfo = false,
  showName = true,
  onPress,
  showLoadingIndicator = true,
  placeholder = 'Usuario'
}) {
  // Usar el hook centralizado para obtener datos del usuario
  const {
    userProfile,
    basicInfo,
    isLoading,
    error,
    avatar: avatarUtils,
    userId
  } = useCurrentUserProfile();

  /**
   * Maneja la acción de presionar el avatar
   * Ejecuta la función personalizada si se proporciona
   */
  const handleAvatarPress = () => {
    // Ejecutar función personalizada si se proporciona
    if (onPress && typeof onPress === 'function') {
      onPress();
      return;
    }

    // Comportamiento por defecto: navegar al perfil
    // En la web, esto podría abrir el modal de perfil
    console.log('[MyUserAvatar] Avatar presionado - comportamiento por defecto');
  };

  /**
   * Renderiza el estado de carga
   * Muestra un placeholder o indicador de carga según configuración
   */
  const renderLoadingState = () => {
    if (!showLoadingIndicator) {
      return renderPlaceholder();
    }

    return (
      <div className="flex items-center gap-3">
        <div 
          className='bg-neutral-700'
          style={{
            width: customSize,
            height: customSize,
            borderRadius: bradius,
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex'
          }}
        >
          <span style={{ color: '#9CA3AF', fontSize: customSize * 0.3 }}>
            ...
          </span>
        </div>
        {showName && (
          <span className="text-gray-400 opacity-50">
            {placeholder}
          </span>
        )}
      </div>
    );
  };

  /**
   * Renderiza el estado de error
   * Muestra el avatar por defecto con indicación de error
   */
  const renderErrorState = () => (
    <div className="flex items-center gap-3">
      <div
        style={{
          width: customSize,
          height: customSize,
          borderRadius: bradius,
          overflow: 'hidden'
        }}
      >
        <img
          src={defaultImage}
          alt="Avatar por defecto"
          style={{
            width: customSize,
            height: customSize,
            objectFit: 'cover'
          }}
        />
      </div>
      {showName && (
        <span className="text-white opacity-70">
          Error
        </span>
      )}
    </div>
  );

  /**
   * Renderiza un placeholder simple usando CachedAvatarImage con caché
   */
  const renderPlaceholder = () => (
    <div className="flex items-center gap-3">
      <CachedAvatarImage
        userId="placeholder"
        imageUrl={defaultImage}
        customSize={customSize}
        bradius={bradius}
        style={{
          opacity: 0.6
        }}
        showPlaceholder={false}
      />
      {showName && (
        <span className="text-gray-400 opacity-50">
          {placeholder}
        </span>
      )}
    </div>
  );

  /**
   * Renderiza el avatar con información completa usando CachedAvatarImage
   * Usado cuando showInfo=true para mostrar detalles ampliados con caché optimizado
   */
  const renderWithInfo = () => (
    <div
      onClick={handleAvatarPress}
      className="flex items-center gap-4 hover:opacity-80 transition-opacity cursor-pointer"
    >
      <CachedAvatarImage
        userId={userProfile?.id || 'current-user'}
        imageUrl={avatarUtils.getAvatarUrl()}
        customSize={customSize}
        bradius={bradius}
        showPlaceholder={false}
      />
      {showName && (
        <div className="flex flex-col items-center">
          <span className="text-white text-lg font-semibold">
            {basicInfo?.fullName || placeholder}
          </span>
          {basicInfo?.age > 0 && (
            <span className="text-white opacity-70 text-sm">
              {basicInfo.age} años
            </span>
          )}
        </div>
      )}
    </div>
  );

  /**
   * Renderiza el avatar estándar compacto usando CachedAvatarImage
   * Usado para headers y espacios reducidos con caché optimizado
   */
  const renderStandardAvatar = () => (
    <div
      onClick={handleAvatarPress}
      className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
    >
      <CachedAvatarImage
        userId={userProfile?.id || 'current-user'}
        imageUrl={avatarUtils.getAvatarUrl()}
        customSize={customSize}
        bradius={bradius}
        showPlaceholder={false}
      />
      {showName && (
        <span className="text-white text-base">
          {basicInfo?.fullName || placeholder}
        </span>
      )}
    </div>
  );

  /**
   * Renderizado condicional según el estado
   */
  // Si no hay usuario autenticado, mostrar placeholder
  if (!userId) {
    return renderPlaceholder();
  }

  // Estado de carga
  if (isLoading && !userProfile) {
    return renderLoadingState();
  }

  // Estado de error
  if (error && !userProfile) {
    return renderErrorState();
  }

  // Renderizado principal según configuración
  if (showInfo) {
    return renderWithInfo();
  }

  return renderStandardAvatar();
}
